/**
 * Team management service — wraps /api/v1/team endpoints.
 *
 * Roles map 1:1 to backend ``Role`` enum (lowercase strings):
 *   owner | admin | member | viewer
 */
import api from './api';

export interface ServiceResult<T> {
  data?: T;
  error?: string;
}

function extractError(err: any, fallback: string): string {
  return (
    err?.response?.data?.message ||
    err?.response?.data?.detail ||
    err?.message ||
    fallback
  );
}

export type Role = 'owner' | 'admin' | 'member' | 'viewer';

export interface OrgOut {
  id: number;
  name: string;
  slug: string;
}

export interface MemberOut {
  user_id: number;
  email: string;
  full_name: string | null;
  role: Role;
  is_owner: boolean;
  is_self: boolean;
}

export interface InviteOut {
  id: number;
  email: string;
  role: Role;
  expires_at: string;
  created_at: string;
}

export interface TeamView {
  organization: OrgOut;
  my_role: Role;
  members: MemberOut[];
  pending_invites: InviteOut[];
}

export async function getTeam(): Promise<ServiceResult<TeamView>> {
  try {
    const { data } = await api.get<TeamView>('/team');
    return { data };
  } catch (err) {
    return { error: extractError(err, 'Failed to load team') };
  }
}

export async function inviteMember(
  email: string,
  role: Role,
  message?: string,
): Promise<ServiceResult<InviteOut>> {
  try {
    const { data } = await api.post<InviteOut>('/team/invites', {
      email, role, message: message || null,
    });
    return { data };
  } catch (err) {
    return { error: extractError(err, 'Failed to send invite') };
  }
}

export async function updateMemberRole(
  userId: number,
  role: Role,
): Promise<ServiceResult<{ user_id: number; role: string }>> {
  try {
    const { data } = await api.patch(`/team/members/${userId}`, { role });
    return { data };
  } catch (err) {
    return { error: extractError(err, 'Failed to update role') };
  }
}

export async function removeMember(
  userId: number,
): Promise<ServiceResult<{ removed: number }>> {
  try {
    const { data } = await api.delete(`/team/members/${userId}`);
    return { data };
  } catch (err) {
    return { error: extractError(err, 'Failed to remove member') };
  }
}

export async function revokeInvite(
  inviteId: number,
): Promise<ServiceResult<{ revoked: number }>> {
  try {
    const { data } = await api.delete(`/team/invites/${inviteId}`);
    return { data };
  } catch (err) {
    return { error: extractError(err, 'Failed to revoke invite') };
  }
}

export async function acceptInvite(
  token: string,
  fullName?: string,
): Promise<ServiceResult<TeamView>> {
  try {
    const { data } = await api.post<TeamView>(
      `/team/invites/${token}/accept`,
      { full_name: fullName || null },
    );
    return { data };
  } catch (err) {
    return { error: extractError(err, 'Failed to accept invite') };
  }
}

const teamService = {
  getTeam,
  inviteMember,
  updateMemberRole,
  removeMember,
  revokeInvite,
  acceptInvite,
};

export default teamService;
